import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { api } from '@/App';
import { 
  Phone, 
  Loader2, 
  Shield,
  ArrowRight,
  User,
  X,
  ArrowLeft
} from 'lucide-react';

const PortalLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnUrl = new URLSearchParams(location.search).get('returnUrl') || '/';
  
  const [step, setStep] = useState('phone'); // phone, otp
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [guestData, setGuestData] = useState(null);

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    
    if (!phone.trim() || phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    try {
      // First check if guest exists
      const checkRes = await api.get(`/portal/auth/check-guest?phone=${encodeURIComponent(phone.trim())}`);
      
      if (!checkRes.data.exists) {
        // Guest not found - redirect to signup
        toast.error('Account not found. Please sign up first.');
        navigate(`/signup?phone=${encodeURIComponent(phone)}&returnUrl=${encodeURIComponent(returnUrl)}`);
        return;
      }
      
      // Guest exists - send OTP
      const response = await api.post('/portal/auth/request-otp', {
        phone: phone.trim(),
        name: '', // Not needed for existing users
        email: '' // Not needed for existing users
      });
      
      setGuestData(response.data);
      setStep('otp');
      toast.success('OTP sent to your phone');
      
      // For development - show OTP in console
      if (response.data.otp) {
        console.log('Development OTP:', response.data.otp);
        toast.info(`Development OTP: ${response.data.otp}`, { duration: 10000 });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    if (!otp || otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/portal/auth/verify-otp', {
        phone: phone.trim(),
        otp: otp
      });
      
      // Save session
      localStorage.setItem('portal_token', response.data.token);
      localStorage.setItem('portal_guest', JSON.stringify(response.data.guest));
      
      toast.success('Login successful!');
      navigate(returnUrl);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md relative">
        {/* Close / Back button */}
        <button 
          onClick={() => navigate(-1)}
          className="absolute top-4 right-4 z-10 p-1.5 rounded-full hover:bg-gray-100 transition-colors"
          data-testid="login-close-btn"
        >
          <X className="h-5 w-5 text-gray-400 hover:text-gray-600" />
        </button>
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-orange-100 rounded-full mx-auto mb-4 flex items-center justify-center">
            {step === 'phone' ? (
              <Phone className="h-8 w-8 text-orange-600" />
            ) : (
              <Shield className="h-8 w-8 text-orange-600" />
            )}
          </div>
          <CardTitle className="text-2xl">
            {step === 'phone' ? 'Log In / Sign Up' : 'Verify OTP'}
          </CardTitle>
          <CardDescription>
            {step === 'phone' 
              ? 'Enter your phone number to continue' 
              : `Enter the 6-digit code sent to ${phone}`}
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {step === 'phone' ? (
            <form onSubmit={handleRequestOTP} className="space-y-4">
              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+91 98765 43210"
                    className="pl-10"
                    data-testid="login-phone-input"
                  />
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-orange-500 hover:bg-orange-600"
                disabled={loading}
                data-testid="login-send-otp-btn"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    Send OTP
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </>
                )}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP} className="space-y-4">
              <div>
                <Label htmlFor="otp">Enter OTP</Label>
                <Input
                  id="otp"
                  type="text"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="text-center text-2xl tracking-widest mt-1"
                  data-testid="login-otp-input"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-orange-500 hover:bg-orange-600"
                disabled={loading || otp.length !== 6}
                data-testid="login-verify-otp-btn"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    Verify & Login
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </>
                )}
              </Button>
              
              <Button 
                type="button"
                variant="ghost" 
                className="w-full"
                onClick={() => { setStep('phone'); setOtp(''); }}
              >
                Change Phone Number
              </Button>
            </form>
          )}
          
          <div className="mt-6 text-center text-sm text-gray-500">
            Don&apos;t have an account?{' '}
            <Link 
              to={`/signup?returnUrl=${encodeURIComponent(returnUrl)}`}
              className="text-orange-600 hover:underline font-medium"
            >
              Sign Up
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PortalLogin;
